import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import Layout from '../AppLayout';
import axios from 'axios';

const rotate = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const PageContainer = styled.div`
  padding: 40px 20px 80px;
  max-width: 1400px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 50px;
`;

const PageTitle = styled(motion.h1)`
  font-family: 'Orbitron', sans-serif;
  font-size: 36px;
  background: linear-gradient(135deg, var(--primary), var(--accent));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 15px;
`;

const PageDesc = styled.p`
  color: var(--text-secondary);
  font-size: 16px;
`;

const MainGrid = styled.div`
  display: grid;
  grid-template-columns: 350px 1fr;
  gap: 30px;
  
  @media (max-width: 1000px) {
    grid-template-columns: 1fr;
  }
`;

const Sidebar = styled(motion.div)`
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: 20px;
  padding: 25px;
  height: fit-content;
  position: sticky;
  top: 120px;
`;

const SectionTitle = styled.h4`
  font-family: 'Orbitron', sans-serif;
  font-size: 14px;
  color: var(--primary);
  margin-bottom: 20px;
  text-transform: uppercase;
  letter-spacing: 2px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ModelGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-bottom: 30px;
`;

const ModelCard = styled(motion.div)`
  background: ${props => props.$selected ? 
    'linear-gradient(135deg, rgba(0, 240, 255, 0.2), rgba(123, 44, 191, 0.2))' : 
    'var(--bg-dark)'};
  border: 2px solid ${props => props.$selected ? 'var(--primary)' : 'var(--border-color)'};
  border-radius: 12px;
  padding: 15px;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: center;
  
  &:hover {
    border-color: var(--primary);
    transform: translateY(-2px);
  }
`;

const ModelImage = styled.div`
  width: 80px;
  height: 80px;
  margin: 0 auto 10px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--primary), var(--secondary));
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
`;

const ModelName = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
`;

const ModelSex = styled.div`
  font-size: 11px;
  color: var(--text-secondary);
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  font-size: 13px;
  color: var(--text-secondary);
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const Select = styled.select`
  width: 100%;
  padding: 12px 14px;
  background: var(--bg-dark);
  border: 1px solid var(--border-color);
  border-radius: 10px;
  color: var(--text-primary);
  font-size: 14px;
  cursor: pointer;
  
  &:focus {
    border-color: var(--primary);
    box-shadow: 0 0 15px rgba(0, 240, 255, 0.3);
    outline: none;
  }
  
  option {
    background: var(--bg-dark);
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 14px;
  background: var(--bg-dark);
  border: 1px solid var(--border-color);
  border-radius: 10px;
  color: var(--text-primary);
  font-size: 14px;
  
  &:focus {
    border-color: var(--primary);
    box-shadow: 0 0 15px rgba(0, 240, 255, 0.3);
    outline: none;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: 14px;
  background: var(--bg-dark);
  border: 1px solid var(--border-color);
  border-radius: 10px;
  color: var(--text-primary);
  font-size: 14px;
  resize: vertical;
  
  &:focus {
    border-color: var(--primary);
    box-shadow: 0 0 15px rgba(0, 240, 255, 0.3);
    outline: none;
  }
`;

const SliderGroup = styled.div`
  margin-bottom: 20px;
`;

const SliderHeader = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const SliderValue = styled.span`
  font-family: 'Orbitron', sans-serif;
  font-size: 12px;
  color: var(--primary);
`;

const Slider = styled.input`
  width: 100%;
  height: 6px;
  -webkit-appearance: none;
  background: var(--bg-dark);
  border-radius: 3px;
  
  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: 16px;
    height: 16px;
    background: linear-gradient(135deg, var(--primary), var(--secondary));
    border-radius: 50%;
    cursor: pointer;
  }
`;

const ActionBtn = styled(motion.button)`
  width: 100%;
  padding: 16px;
  background: ${props => props.$primary ? 
    'linear-gradient(135deg, var(--primary), var(--secondary))' : 
    'transparent'};
  border: ${props => props.$primary ? 'none' : '2px solid var(--primary)'};
  border-radius: 12px;
  color: #fff;
  font-family: 'Orbitron', sans-serif;
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 2px;
  cursor: pointer;
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PreviewArea = styled(motion.div)`
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: 20px;
  overflow: hidden;
  position: relative;
  min-height: 600px;
`;

const PreviewHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 25px;
  border-bottom: 1px solid var(--border-color);
`;

const PreviewTitle = styled.h3`
  font-family: 'Orbitron', sans-serif;
  font-size: 16px;
  color: var(--primary);
`;

const PreviewContent = styled.div`
  padding: 30px;
  min-height: 500px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const HumanAvatar = styled(motion.div)`
  width: 200px;
  height: 200px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--primary), var(--secondary), var(--accent));
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 80px;
  margin-bottom: 30px;
  box-shadow: 0 0 60px rgba(0, 240, 255, 0.4);
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    inset: -5px;
    border-radius: 50%;
    background: linear-gradient(135deg, var(--primary), var(--accent));
    z-index: -1;
    filter: blur(20px);
    opacity: 0.5;
  }
`;

const PreviewText = styled.p`
  color: var(--text-secondary);
  font-size: 16px;
  text-align: center;
  max-width: 400px;
  line-height: 1.8;
`;

const VideoPlayer = styled.video`
  width: 100%;
  max-width: 800px;
  border-radius: 12px;
  box-shadow: 0 0 40px rgba(0, 240, 255, 0.3);
`;

const StatusIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 20px;
  border-radius: 25px;
  background: ${props => {
    switch(props.$status) {
      case 'success': return 'rgba(0, 255, 136, 0.15)';
      case 'processing': return 'rgba(255, 170, 0, 0.15)';
      case 'error': return 'rgba(255, 51, 102, 0.15)';
      default: return 'rgba(160, 160, 176, 0.15)';
    }
  }};
`;

const StatusDot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${props => {
    switch(props.$status) {
      case 'success': return 'var(--success)';
      case 'processing': return 'var(--warning)';
      case 'error': return 'var(--danger)';
      default: return 'var(--text-secondary)';
    }
  }};
  animation: ${props => props.$status === 'processing' ? rotate : 'none'} 1s linear infinite;
`;

const LoadingSpinner = styled.div`
  width: 60px;
  height: 60px;
  border: 3px solid var(--border-color);
  border-top-color: var(--primary);
  border-radius: 50%;
  animation: ${rotate} 1s linear infinite;
`;

const ProgressBar = styled.div`
  width: 100%;
  max-width: 400px;
  height: 6px;
  background: var(--bg-dark);
  border-radius: 3px;
  overflow: hidden;
  margin-top: 20px;
`;

const Progress = styled(motion.div)`
  height: 100%;
  background: linear-gradient(90deg, var(--primary), var(--accent));
  border-radius: 3px;
`;

// 数字人形象将从API动态获取

const voiceTypes = [
  { value: '1', label: 'AI模型配音' },
  { value: '2', label: '音频文件URL' },
  { value: '3', label: '我的克隆声音' },
];

const VideoPage = () => {
  const [digitalHumans, setDigitalHumans] = useState([]);
  const [selectedHuman, setSelectedHuman] = useState(null);
  const [voiceType, setVoiceType] = useState('1');
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [publicVoices, setPublicVoices] = useState([]);
  const [myVoices, setMyVoices] = useState([]);
  const [content, setContent] = useState('');
  const [speed, setSpeed] = useState(1.0);
  const [pitch, setPitch] = useState(1.0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [status, setStatus] = useState('idle');
  const [loading, setLoading] = useState(true);
  const [selectedPlatform, setSelectedPlatform] = useState('Pro'); // 默认选择Pro商业版
  const [videoPlayer, setVideoPlayer] = useState({
    isOpen: false,
    videoUrl: null,
    title: null
  });

  // 获取数字人形象列表
  const fetchDigitalHumans = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      console.log('获取数字人列表, platform:', selectedPlatform);
      
      const response = await axios.get('/api/v1/digital_human/public/models', {
        params: {
          platform_code: selectedPlatform
        },
        headers: { 'Authorization': `Bearer ${token}` }
      });
      console.log('数字人列表响应:', response.data);
      
      if (response.data.code === 1 && response.data.data && Array.isArray(response.data.data)) {
        const humans = response.data.data.map(human => {
          let videoSnapshot = null;
          let videoOssUrl = human.video_url || human.video_oss_url || '';
          if (videoOssUrl && videoOssUrl.includes('.mp4')) {
            const separator = videoOssUrl.includes('?') ? '&' : '?';
            videoSnapshot = `${videoOssUrl}${separator}x-oss-process=video/snapshot,t_1000,f_jpg`;
          }
          return {
            id: human.id,
            name: human.name,
            sex: human.sex === 1 ? '男' : '女',
            icon: human.sex === 1 ? '👨' : '👩',
            platform_name: human.platform_name,
            platform_code: selectedPlatform,
            video_oss_url: videoOssUrl,
            video_snapshot: videoSnapshot,
            model_group_id: human.model_group_id || 1
          };
        });
        console.log('解析后数字人列表:', humans);
        setDigitalHumans(humans);
        if (humans.length > 0 && !selectedHuman) {
          setSelectedHuman(humans[0]);
        }
      } else {
        console.warn('公模列表为空或返回格式异常:', response.data);
        setDigitalHumans([]);
      }
    } catch (error) {
      console.error('获取数字人形象失败:', error);
    } finally {
      setLoading(false);
    }
  };

  // 获取配音音色列表
  const fetchVoices = async () => {
    try {
      const token = localStorage.getItem('token');
      
      const [publicRes, myRes, historyRes] = await Promise.all([
        axios.get('/api/v1/voice/list', {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        axios.get('/api/v1/voice/my-voices', {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        axios.get('/api/v1/voice/history', {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      console.log('公模声音:', publicRes.data);
      console.log('我的声音:', myRes.data);
      console.log('历史记录:', historyRes.data);
      
      const publicVoiceList = publicRes.data.code === 1 && publicRes.data.data 
        ? publicRes.data.data.map(v => ({
            id: v.voice_id || v.id,
            name: v.voice_name || v.name || `公模声音${v.voice_id || v.id}`,
            sex: v.sex === 1 || v.sex === '男' ? '男' : '女',
            audio_url: v.audio_oss_url || v.url || '',
            source: 'public'
          }))
        : [];
      
      const myVoiceList = myRes.data.code === 1 && myRes.data.data
        ? myRes.data.data.map(v => ({
            id: v.voice_id || v.id,
            name: v.voice_name || v.name || `我的声音${v.voice_id || v.id}`,
            sex: v.sex === 1 || v.sex === '男' ? '男' : '女',
            audio_url: v.audio_oss_url || v.url || '',
            source: 'my'
          }))
        : [];
      
      const historyVoiceList = historyRes.data.code === 1 && historyRes.data.data
        ? historyRes.data.data.filter(v => v.status === 1 && v.audio_url).map(v => ({
            id: v.id,
            name: v.text_content?.substring(0, 20) || `历史录音${v.id}`,
            sex: '未知',
            audio_url: v.audio_url,
            source: 'history'
          }))
        : [];
      
      setPublicVoices(publicVoiceList);
      setMyVoices([...myVoiceList, ...historyVoiceList]);
      
      if (publicVoiceList.length > 0) {
        setSelectedVoice(publicVoiceList[0]);
      } else if (myVoiceList.length > 0 || historyVoiceList.length > 0) {
        setSelectedVoice((myVoiceList[0] || historyVoiceList[0]));
      }
    } catch (error) {
      console.error('获取配音音色失败:', error);
    }
  };

  // 根据配音类型获取对应的声音列表
  const getCurrentVoices = () => {
    if (voiceType === '2' || voiceType === '3') {
      return myVoices;
    }
    return publicVoices;
  };

  // 页面加载时获取数字人形象
  useEffect(() => {
    fetchDigitalHumans();
    fetchVoices();
  }, [selectedPlatform]);
  
  // 初始化时从URL读取平台参数
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const presetPlatformCode = params.get('platform_code');
    if (presetPlatformCode) {
      setSelectedPlatform(presetPlatformCode);
    }
  }, []);

  // 打开视频播放器
  const openVideoPlayer = (videoUrl, title) => {
    setVideoPlayer({
      isOpen: true,
      videoUrl,
      title
    });
  };

  // 关闭视频播放器
  const closeVideoPlayer = () => {
    setVideoPlayer({
      isOpen: false,
      videoUrl: null,
      title: null
    });
  };

  const handleGenerate = async () => {
    if (!content.trim()) {
      alert('请输入视频文案');
      return;
    }

    if (!selectedHuman) {
      alert('请选择数字人形象');
      return;
    }

    if (voiceType !== '2' && !selectedVoice) {
      alert('请选择配音音色');
      return;
    }

    setIsGenerating(true);
    setStatus('processing');
    setResult(null);

    let requestData = {
      voice_type: '2', // 固定为2，根据API文档要求
      model_type: 1, // 系统公模形象
      model_id: selectedHuman?.id || 1,
      platform_code: selectedPlatform,
      text: content, // 添加文本字段用于AI配音
      voice_model_type: '1' // 添加声音模型类型字段
    };

    if (voiceType === '1' && selectedVoice?.id) {
      // AI模型配音
      requestData.voice_id = selectedVoice.id;
    } else if (voiceType === '2' || voiceType === '3') {
      // 音频文件URL或我的克隆声音
      if (selectedVoice?.audio_url) {
        requestData.audio_oss_url = selectedVoice.audio_url;
      } else {
        alert('请选择有效的音频文件');
        setIsGenerating(false);
        return;
      }
    }

    try {
      const response = await axios.post('/api/v1/digital_human/merge', requestData, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      console.log('视频合成响应:', response.data);

      if (response.data.code === 1) {
        setResult(response.data.data);
        setStatus('success');
      } else {
        console.error('视频合成失败:', response.data.msg);
        alert(response.data.msg || '视频合成失败');
        setStatus('error');
      }
    } catch (error) {
      console.error('API调用失败:', error);
      console.error('错误详情:', error.response?.data);
      alert(error.response?.data?.detail || error.message || '合成失败，请检查网络连接');
      setStatus('error');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Layout>
      {/* 视频播放器模态框 */}
      {videoPlayer.isOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'rgba(0, 0, 0, 0.85)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          backdropFilter: 'blur(10px)'
        }}
        onClick={closeVideoPlayer}
        >
          <div style={{
            width: '90%',
            maxWidth: '900px',
            background: 'var(--bg-dark)',
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
            position: 'relative'
          }}
          onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              padding: '20px',
              borderBottom: '1px solid var(--border-color)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{ 
                color: 'var(--text-primary)', 
                margin: 0,
                fontSize: '18px'
              }}>
                🎬 {videoPlayer.title}
              </h3>
              <button 
                onClick={closeVideoPlayer}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--text-secondary)',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '5px',
                  borderRadius: '4px',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => e.target.style.color = 'var(--danger)'}
                onMouseLeave={(e) => e.target.style.color = 'var(--text-secondary)'}
              >
                ✕
              </button>
            </div>
            
            <div style={{
              padding: '20px',
              textAlign: 'center'
            }}>
              {videoPlayer.videoUrl ? (
                <video
                  controls
                  autoPlay
                  style={{
                    width: '100%',
                    maxHeight: '70vh',
                    borderRadius: '8px',
                    outline: 'none'
                  }}
                >
                  <source src={videoPlayer.videoUrl} type="video/mp4" />
                  您的浏览器不支持视频播放
                </video>
              ) : (
                <div style={{
                  padding: '60px',
                  color: 'var(--text-secondary)',
                  fontSize: '16px'
                }}>
                  视频链接无效
                </div>
              )}
            </div>
            
            <div style={{
              padding: '15px 20px',
              borderTop: '1px solid var(--border-color)',
              textAlign: 'right'
            }}>
              <button
                onClick={() => {
                  if (videoPlayer.videoUrl) {
                    window.open(videoPlayer.videoUrl, '_blank');
                  }
                }}
                style={{
                  background: 'var(--primary)',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                onMouseLeave={(e) => e.target.style.opacity = '1'}
              >
                在新标签页打开
              </button>
            </div>
          </div>
        </div>
      )}

      <PageContainer>
        <PageHeader>
          <PageTitle
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            数字人视频生成
          </PageTitle>
          <PageDesc>选择数字人形象，配置语音参数，一键生成AI数字人视频</PageDesc>
        </PageHeader>

        <MainGrid>
          <Sidebar
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <SectionTitle>
              <span>🎭</span> 选择数字人形象
            </SectionTitle>
            
            {/* 平台选择器 */}
            <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>选择数字人版本：</span>
              <button 
                onClick={() => setSelectedPlatform('Pro')}
                style={{
                  padding: '6px 12px',
                  background: selectedPlatform === 'Pro' ? 'var(--primary)' : 'rgba(0, 0, 0, 0.3)',
                  border: selectedPlatform === 'Pro' ? '1px solid var(--primary)' : '1px solid var(--border-color)',
                  borderRadius: '6px',
                  color: selectedPlatform === 'Pro' ? 'white' : 'var(--text-secondary)',
                  cursor: 'pointer',
                  fontSize: '13px',
                  transition: 'all 0.3s ease'
                }}
              >
                Pro商业版
              </button>
              <button 
                onClick={() => setSelectedPlatform('VPro')}
                style={{
                  padding: '6px 12px',
                  background: selectedPlatform === 'VPro' ? 'var(--primary)' : 'rgba(0, 0, 0, 0.3)',
                  border: selectedPlatform === 'VPro' ? '1px solid var(--primary)' : '1px solid var(--border-color)',
                  borderRadius: '6px',
                  color: selectedPlatform === 'VPro' ? 'white' : 'var(--text-secondary)',
                  cursor: 'pointer',
                  fontSize: '13px',
                  transition: 'all 0.3s ease'
                }}
              >
                VPro高清版
              </button>
            </div>
            
            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>加载数字人形象中...</div>
              </div>
            ) : digitalHumans.length > 0 ? (
              <ModelGrid>
                {digitalHumans.map((human) => (
                  <ModelCard
                    key={human.id}
                    $selected={selectedHuman?.id === human.id}
                    onClick={() => setSelectedHuman(human)}
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <ModelImage>
                      {human.video_snapshot || human.video_oss_url ? (
                        <div 
                          onClick={(e) => {
                            e.stopPropagation();
                            openVideoPlayer(human.video_oss_url, human.name);
                          }}
                          style={{ 
                            display: 'block', 
                            width: '100%', 
                            height: '100%',
                            cursor: 'pointer',
                            position: 'relative',
                            borderRadius: '50%',
                            overflow: 'hidden'
                          }}
                        >
                          {human.video_snapshot ? (
                            <img 
                              src={human.video_snapshot} 
                              alt={human.name}
                              style={{ 
                                width: '100%', 
                                height: '100%', 
                                objectFit: 'cover',
                                borderRadius: '50%'
                              }}
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.style.display = 'none';
                                if (e.target.parentElement) {
                                  e.target.parentElement.innerHTML = `
                                    <div style="display:flex;align-items:center;justify-content:center;height:100%;flex-direction:column;gap:5px;background:linear-gradient(135deg, var(--primary), var(--secondary));">
                                      <span style="font-size:28px;">🎬</span>
                                      <span style="font-size:10px;color:white;opacity:0.8;">点击播放</span>
                                    </div>
                                  `;
                                }
                              }}
                            />
                          ) : (
                            <div style={{
                              width: '100%',
                              height: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexDirection: 'column',
                              gap: '5px',
                              background: 'linear-gradient(135deg, var(--primary), var(--secondary))'
                            }}>
                              <span style={{ fontSize: '28px' }}>🎬</span>
                              <span style={{ fontSize: '10px', color: 'white', opacity: 0.8 }}>点击播放</span>
                            </div>
                          )}
                          <div style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: 'rgba(0, 0, 0, 0)',
                            transition: 'background 0.3s ease',
                            borderRadius: '50%'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0, 0, 0, 0.3)'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(0, 0, 0, 0)'}
                          >
                            <div style={{
                              background: 'rgba(0, 0, 0, 0.7)',
                              borderRadius: '50%',
                              width: '36px',
                              height: '36px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              opacity: 0,
                              transition: 'opacity 0.3s ease'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                            onMouseLeave={(e) => e.currentTarget.style.opacity = '0'}
                            >
                              <span style={{ color: 'white', fontSize: '16px' }}>▶</span>
                            </div>
                          </div>
                        </div>
                      ) : (
                        human.icon
                      )}
                    </ModelImage>
                    <ModelName>{human.name}</ModelName>
                    <ModelSex>{human.sex}</ModelSex>
                    <div style={{ fontSize: '11px', color: human.platform_code === 'Pro' ? '#00f0ff' : '#ff6b6b', marginTop: '4px' }}>
                      {human.platform_code === 'Pro' ? 'Pro商业版' : 'VPro高清版'}
                    </div>
                  </ModelCard>
                ))}
              </ModelGrid>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>暂无数字人形象，请先创建或克隆数字人</div>
              </div>
            )}

            <SectionTitle>
              <span>🎙️</span> 配音配置
            </SectionTitle>
            
            <FormGroup>
              <Label>配音方式</Label>
              <Select value={voiceType} onChange={(e) => setVoiceType(e.target.value)}>
                {voiceTypes.map((type) => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </Select>
            </FormGroup>

            {voiceType === '1' && (
              <FormGroup>
                <Label>选择公模音色</Label>
                <Select 
                  value={selectedVoice?.id || ''} 
                  onChange={(e) => {
                    const voice = publicVoices.find(v => v.id === parseInt(e.target.value));
                    setSelectedVoice(voice);
                  }}
                >
                  <option value="">请选择音色</option>
                  {publicVoices.map((v) => (
                    <option key={v.id} value={v.id}>{v.name} ({v.sex})</option>
                  ))}
                </Select>
              </FormGroup>
            )}

            {voiceType === '2' && (
              <>
                <FormGroup>
                  <Label>选择音频文件</Label>
                  <Select 
                    value={selectedVoice?.id || ''} 
                    onChange={(e) => {
                      const voice = myVoices.find(v => v.id === parseInt(e.target.value));
                      setSelectedVoice(voice);
                    }}
                  >
                    <option value="">请选择历史音频</option>
                    {myVoices.map((v) => (
                      <option key={v.id} value={v.id}>{v.name}</option>
                    ))}
                  </Select>
                </FormGroup>
                <FormGroup>
                  <Label>或输入音频URL</Label>
                  <Input 
                    type="text"
                    placeholder="输入音频文件URL"
                    value={selectedVoice?.audio_url || ''}
                    onChange={(e) => {
                      setSelectedVoice({
                        ...selectedVoice,
                        audio_url: e.target.value
                      });
                    }}
                  />
                </FormGroup>
              </>
            )}

            {voiceType === '3' && (
              <FormGroup>
                <Label>选择我的克隆声音</Label>
                <Select 
                  value={selectedVoice?.id || ''} 
                  onChange={(e) => {
                    const voice = myVoices.find(v => v.id === parseInt(e.target.value));
                    setSelectedVoice(voice);
                  }}
                >
                  <option value="">请选择声音</option>
                  {myVoices.map((v) => (
                    <option key={v.id} value={v.id}>{v.name} ({v.sex})</option>
                  ))}
                </Select>
              </FormGroup>
            )}

            <SectionTitle>
              <span>⚡</span> 语音参数
            </SectionTitle>

            <SliderGroup>
              <SliderHeader>
                <Label>语速</Label>
                <SliderValue>{speed.toFixed(1)}x</SliderValue>
              </SliderHeader>
              <Slider 
                type="range" 
                min="0.5" 
                max="2.0" 
                step="0.1" 
                value={speed}
                onChange={(e) => setSpeed(parseFloat(e.target.value))}
              />
            </SliderGroup>

            <SliderGroup>
              <SliderHeader>
                <Label>音调</Label>
                <SliderValue>{pitch.toFixed(1)}</SliderValue>
              </SliderHeader>
              <Slider 
                type="range" 
                min="0.5" 
                max="2.0" 
                step="0.1" 
                value={pitch}
                onChange={(e) => setPitch(parseFloat(e.target.value))}
              />
            </SliderGroup>

            <SectionTitle>
              <span>📝</span> 视频文案
            </SectionTitle>
            
            <TextArea 
              placeholder="请输入视频文案内容...&#10;AI将自动匹配数字人嘴型"
              value={content}
              onChange={(e) => setContent(e.target.value)}
            />

            <ActionBtn 
              onClick={handleGenerate}
              disabled={isGenerating || !content.trim()}
              $primary
              style={{ marginTop: '20px' }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {isGenerating ? '生成中...' : '生成视频'}
            </ActionBtn>
          </Sidebar>

          <PreviewArea
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <PreviewHeader>
              <PreviewTitle>预览区域</PreviewTitle>
              <StatusIndicator $status={status}>
                <StatusDot $status={status} />
                <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                  {status === 'idle' && '等待生成'}
                  {status === 'processing' && '生成中'}
                  {status === 'success' && '生成成功'}
                  {status === 'error' && '生成失败'}
                </span>
              </StatusIndicator>
            </PreviewHeader>

            <PreviewContent>
              {status === 'idle' && (
                <>
                  <HumanAvatar
                    animate={{ 
                      scale: [1, 1.05, 1],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                  >
                    {selectedHuman ? (
                      selectedHuman.video_snapshot || selectedHuman.video_oss_url ? (
                        <div 
                          onClick={() => openVideoPlayer(selectedHuman.video_oss_url, selectedHuman.name)}
                          style={{ 
                            display: 'block', 
                            width: '100%', 
                            height: '100%',
                            cursor: 'pointer',
                            position: 'relative',
                            borderRadius: '50%',
                            overflow: 'hidden'
                          }}
                        >
                          {selectedHuman.video_snapshot ? (
                            <img 
                              src={selectedHuman.video_snapshot} 
                              alt={selectedHuman.name}
                              style={{ 
                                width: '100%', 
                                height: '100%', 
                                objectFit: 'cover',
                                borderRadius: '50%'
                              }}
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.style.display = 'none';
                                if (e.target.parentElement) {
                                  e.target.parentElement.innerHTML = `
                                    <div style="display:flex;align-items:center;justify-content:center;height:100%;flex-direction:column;gap:5px;background:linear-gradient(135deg, var(--primary), var(--secondary));">
                                      <span style="font-size:32px;">🎬</span>
                                      <span style="font-size:12px;color:white;opacity:0.8;">点击播放</span>
                                    </div>
                                  `;
                                }
                              }}
                            />
                          ) : (
                            <div style={{
                              width: '100%',
                              height: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexDirection: 'column',
                              gap: '5px',
                              background: 'linear-gradient(135deg, var(--primary), var(--secondary))'
                            }}>
                              <span style={{ fontSize: '32px' }}>🎬</span>
                              <span style={{ fontSize: '12px', color: 'white', opacity: 0.8 }}>点击播放</span>
                            </div>
                          )}
                          <div style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: 'rgba(0, 0, 0, 0)',
                            transition: 'background 0.3s ease',
                            borderRadius: '50%'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0, 0, 0, 0.3)'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(0, 0, 0, 0)'}
                          >
                            <div style={{
                              background: 'rgba(0, 0, 0, 0.7)',
                              borderRadius: '50%',
                              width: '50px',
                              height: '50px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              opacity: 0,
                              transition: 'opacity 0.3s ease'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                            onMouseLeave={(e) => e.currentTarget.style.opacity = '0'}
                            >
                              <span style={{ color: 'white', fontSize: '22px' }}>▶</span>
                            </div>
                          </div>
                        </div>
                      ) : (
                        selectedHuman.icon
                      )
                    ) : '👤'}
                  </HumanAvatar>
                  <PreviewText>
                    {selectedHuman ? (
                      <>
                        选择数字人形象和配音<br />
                        输入视频文案<br />
                        点击"生成视频"按钮开始创作
                      </>
                    ) : (
                      <>
                        加载数字人形象中...<br />
                        请稍候
                      </>
                    )}
                  </PreviewText>
                </>
              )}

              {status === 'processing' && (
                <>
                  <LoadingSpinner />
                  <PreviewText style={{ marginTop: '30px' }}>
                    正在合成数字人视频<br />
                    请稍候...
                  </PreviewText>
                  <ProgressBar>
                    <Progress 
                      initial={{ width: 0 }}
                      animate={{ width: '100%' }}
                      transition={{ duration: 10 }}
                    />
                  </ProgressBar>
                </>
              )}

              {status === 'success' && result && (
                <>
                  <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                    <h3 style={{ color: 'var(--text-primary)', marginBottom: '15px' }}>🎬 成片预览</h3>
                    <p style={{ color: 'var(--text-secondary)', fontSize: '14px', marginBottom: '20px' }}>
                      视频合成任务已提交，请查看您的作品
                    </p>
                  </div>
                  
                  <div style={{ 
                    background: 'rgba(0, 0, 0, 0.3)', 
                    borderRadius: '12px', 
                    padding: '20px',
                    marginBottom: '20px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
                      <div style={{ 
                        width: '60px', 
                        height: '60px', 
                        borderRadius: '8px',
                        overflow: 'hidden',
                        marginRight: '15px',
                        flexShrink: 0
                      }}>
                        {result[0]?.video_oss_url ? (
                          <a 
                            href={result[0]?.video_oss_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            style={{ display: 'block', width: '100%', height: '100%' }}
                          >
                            <img 
                              src={result[0]?.video_oss_url} 
                              alt="视频封面"
                              style={{ 
                                width: '100%', 
                                height: '100%', 
                                objectFit: 'cover'
                              }}
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.style.display = 'none';
                                if (e.target.parentElement) {
                                  e.target.parentElement.innerHTML = '🎬';
                                }
                              }}
                            />
                          </a>
                        ) : (
                          <div style={{ 
                            width: '100%', 
                            height: '100%', 
                            background: 'var(--primary)', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            fontSize: '24px'
                          }}>
                            🎬
                          </div>
                        )}
                      </div>
                      <div>
                        <h4 style={{ color: 'var(--text-primary)', marginBottom: '5px' }}>合成视频作品</h4>
                        <p style={{ color: 'var(--text-secondary)', fontSize: '13px', marginBottom: '8px' }}>
                          状态：{result[0]?.status === 1 ? '✅ 已完成' : '⏳ 处理中'}
                        </p>
                        {result[0]?.video_oss_url && (
                          <button 
                            onClick={() => openVideoPlayer(result[0]?.video_oss_url, '合成视频作品')}
                            style={{ 
                              color: 'var(--primary)', 
                              fontSize: '13px',
                              textDecoration: 'none',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '5px',
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              padding: 0
                            }}
                          >
                            <span>▶</span> 播放视频
                          </button>
                        )}
                      </div>
                    </div>
                    
                    <VideoPlayer 
                      controls 
                      autoPlay
                      src={result[0]?.video_oss_url}
                      style={{ marginTop: '15px' }}
                    />
                  </div>
                  
                  <PreviewText style={{ marginTop: '20px' }}>
                    视频生成成功！<br />
                    您可以下载或分享您的作品
                  </PreviewText>
                </>
              )}

              {status === 'error' && (
                <>
                  <HumanAvatar style={{ background: 'linear-gradient(135deg, var(--danger), var(--warning))' }}>
                    ⚠️
                  </HumanAvatar>
                  <PreviewText>
                    视频生成失败<br />
                    请检查网络连接或稍后重试
                  </PreviewText>
                </>
              )}
            </PreviewContent>
          </PreviewArea>
        </MainGrid>
      </PageContainer>
    </Layout>
  );
};

export default VideoPage;
